/**
 * WebSocket接続・メッセージハンドラ管理フック
 * interview.tsxのWS接続ロジックを抽出
 */

import { useEffect, useRef } from "react";
import { Avatar } from "../types";
import {
  createBrowserInterviewWebSocketRuntimeEnvironment,
  startInterviewWebSocketRuntime,
} from './interviewWebSocketRuntime';
import type { SharedRefs, WebSocketCallbacks } from './interviewWebSocketTypes';

export type { SttCorrectionDraft, SttCorrectionDraftDisposition } from "./interviewSttMessageController";
export type {
  CounterpartPresentation,
  SharedRefs,
  MappedVisemeEvent,
  WebSocketCallbacks,
} from './interviewWebSocketTypes';

export function useInterviewWebSocket(
  currentAvatar: Avatar | undefined,
  refs: SharedRefs,
  callbacks: WebSocketCallbacks,
) {
  const {
    wsClientRef,
    sessionIdRef,
    recognitionClientIdRef,
    sttSessionMessageIdRef,
    sttPreviousInterimTextRef,
    sttConfirmedTextRef,
    sttReadyRef,
  } = refs;
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const avatarId = currentAvatar?.id;
  const interviewId = (currentAvatar as { interviewId?: number | null })?.interviewId ?? null;
  const avatarType = (currentAvatar as any)?.type || 'patient';
  const avatarDubbingVideo = (currentAvatar as any)?.videoFiles?.dubbingVideo;
  const avatarGender = currentAvatar?.gender;
  const avatarAge = currentAvatar?.age;

  useEffect(() => {
    if (avatarId == null) return;

    const getCallbacks = () => callbacksRef.current;
    const controllerRefs: SharedRefs = {
      wsClientRef,
      sessionIdRef,
      recognitionClientIdRef,
      sttSessionMessageIdRef,
      sttPreviousInterimTextRef,
      sttConfirmedTextRef,
      sttReadyRef,
    };
    const runtime = startInterviewWebSocketRuntime({
      refs: controllerRefs,
      getCallbacks,
      avatar: {
        avatarId,
        interviewId,
        avatarType,
        dubbingVideo: avatarDubbingVideo,
        gender: avatarGender,
        age: avatarAge,
      },
      environment: createBrowserInterviewWebSocketRuntimeEnvironment(),
    });

    return runtime.cleanup;
  }, [
    avatarAge,
    avatarDubbingVideo,
    avatarGender,
    avatarId,
    avatarType,
    interviewId,
    recognitionClientIdRef,
    sessionIdRef,
    sttConfirmedTextRef,
    sttPreviousInterimTextRef,
    sttReadyRef,
    sttSessionMessageIdRef,
    wsClientRef,
  ]);
}
